import { trpc } from '@/utils/trpc';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import useEvent from './useEvent';
import { EVENTS } from '@/utils/constants';
import { captureException } from '@sentry/nextjs';
import useNotification from './useNotification';
import { showOwnershipTipModalAtom } from '@/store/uiStore';
import { useAtom } from 'jotai';

const useBoardDuplication = () => {
  const router = useRouter();
  const collectionUuid = String(router.query.id);
  if (router.pathname !== '/collection/[id]' || !collectionUuid) {
    return {
      isOwnerOfCollection: false,
      showOwnershipTipModal: false,
      setShowOwnershipTipModal: () => {},
      duplicateBoard: async () => {},
      duplicationIsLoading: false,
    };
  }
  const { capture } = useEvent();
  const { data: collectionData } = trpc.collections.getUserCollectionById.useQuery(collectionUuid, {
    staleTime: Infinity,
    enabled: !!collectionUuid,
  });
  const { refetch: refetchCollections } = trpc.collections.getUserCollections.useQuery(undefined, {
    staleTime: Infinity,
  });
  const { data: user } = trpc.users.getAuthenticatedUser.useQuery(undefined, {
    staleTime: Infinity,
  });
  const [showOwnershipTipModal, setShowOwnershipTipModal] = useAtom(showOwnershipTipModalAtom);
  const duplicateBoardMutation = trpc.collections.duplicateBoard.useMutation();
  const { showNotification } = useNotification();

  const isOwnerOfCollection = useMemo(() => {
    return collectionData?.user_id === user?.id;
  }, [collectionData?.user_id, user?.id]);

  const duplicationIsLoading = duplicateBoardMutation.isLoading;

  async function duplicateBoard(): Promise<void> {
    try {
      if (!collectionData) throw Error('Collection not found');
      await duplicateBoardMutation.mutateAsync({ collectionId: Number(collectionData.id) });
      capture({
        event: EVENTS.copiedMoodboard,
        collection_uuid: collectionUuid ?? '',
        otherInfo: {
          uuid: collectionUuid ?? '',
          userId: user?.id?.toString() ?? '',
        },
      });
      refetchCollections();
      showNotification({ message: 'Copied to My boards', emoji: '🔔' }, 10000);
      window.open('/moodboard?filter=boards', '_blank');
    } catch (err) {
      showNotification({ message: `Something went wrong ${err.message}` }, 6000);
      captureException(err);
    } finally {
      setShowOwnershipTipModal(false);
    }
  }

  return {
    isOwnerOfCollection,
    showOwnershipTipModal,
    setShowOwnershipTipModal,
    duplicateBoard,
    duplicationIsLoading,
  };
};

export default useBoardDuplication;
